import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  Overlay,
  Portal,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { BankIcon, WarningIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { InvoiceApi } from "../../apis";
import { message } from "../../utils/Message";
import { handleError } from "../../utils/error-handler";
import { AppModalHeader, ModalFooter } from "../ui/app-modal";
import modalChrome from "../ui/app-modal.module.css";
import { FeeLine, FeePayLine, money } from "./fee-pay-line";
import { useFeePaymentPolling } from "./use-fee-payment-polling";
import classes from "./fee-pay-modal.module.css";

type GroupFee = FeeLine;
interface TenantReceiveAccount {
  id: string;
  bankCode: string;
  bankName: string | null;
  accountNumber: string;
  accountName: string;
  currency: string;
}
interface TenantGroup {
  tenantId: string;
  tenantReceiveAccount: TenantReceiveAccount | null;
  feeCurrency: string;
  totalAmount: string;
  fees: GroupFee[];
}
interface UnpaidResult {
  groups: TenantGroup[];
  totalUnpaid: string;
  count: number;
}
interface OwnAccount {
  id: string;
  bankName: string;
  name: string;
  number: string;
  currency: string;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  onPaid: () => void;
  /** Pay only this fee (one collected fee); when null, show all owed fees by tenant. */
  scopeFeeId?: string | null;
}

export function FeePayModal({ opened, onClose, onPaid, scopeFeeId }: Props) {
  const { t } = useTranslation(["invoice", "common"]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ownAccounts = useSelector((s: any) => s.general?.bankAccounts ?? []) as OwnAccount[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const myBusinessType = useSelector((s: any) => s.auth?.myBusinessType);
  const poll = useFeePaymentPolling();
  const [loading, setLoading] = useState(false);
  const [payingTenant, setPayingTenant] = useState<string | null>(null);
  const [data, setData] = useState<UnpaidResult | null>(null);
  const [debitId, setDebitId] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    let active = true;
    setLoading(true);
    InvoiceApi.feesUnpaid()
      .then((r) => {
        if (active) setData(r as UnpaidResult);
      })
      .catch((e) => {
        if (active) handleError(e);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [opened]);

  const groups = useMemo<TenantGroup[]>(() => {
    const all = data?.groups ?? [];
    if (!scopeFeeId) return all;
    return all
      .map((g) => ({ ...g, fees: g.fees.filter((f) => f.collectedFeeId === scopeFeeId) }))
      .filter((g) => g.fees.length > 0)
      .map((g) => ({
        ...g,
        totalAmount: String(g.fees.reduce((s, f) => s + Number(f.finalFeeAmount || 0), 0)),
      }));
  }, [data, scopeFeeId]);

  const isEmpty = !loading && groups.length === 0;
  const debit = ownAccounts.find((a) => a.id === debitId) ?? null;
  const debitOptions = ownAccounts.map((a) => ({
    value: a.id,
    label: `${a.bankName} · ${a.number} · ${a.name}`,
  }));

  const collectorName = (g: TenantGroup) =>
    g.fees.find((f) => f.counterpartyName)?.counterpartyName ??
    g.tenantReceiveAccount?.accountName ??
    t("invoice:feePay.collectorFallback");

  const payGroup = async (g: TenantGroup) => {
    if (!g.tenantReceiveAccount || !debit) return;
    setPayingTenant(g.tenantId);
    try {
      const res = (await InvoiceApi.feesPay({
        collectedFeeIds: g.fees.map((f) => f.collectedFeeId),
        creditTenantId: g.tenantId,
        debitAccountId: debit.id,
        debitAccountBank: debit.bankName,
        debitAccountName: debit.name,
        debitAccountNumber: debit.number,
        debitAccountCurrency: debit.currency,
      })) as { url?: string };
      if (res?.url) {
        const popup = window.open(
          res.url,
          "golomt-fee-payment",
          "toolbar=yes,scrollbars=yes,resizable=yes,width=800,height=600",
        );
        if (!popup) {
          setPayingTenant(null);
          message.error(t("invoice:feePay.popupBlocked"));
          return;
        }

        const paidIds = g.fees.map((f) => f.collectedFeeId);
        poll.start({
          popup,
          checkPaid: async () => {
            const closed = (await InvoiceApi.closeWithFee({
              offset: { page: 1, limit: 200 },
              filter: { regNumber: "", isReceived: myBusinessType === "BUYER" },
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any)) as { rows?: any[] };
            const statusById = new Map<string, string>(
              (closed?.rows ?? []).map((r) => [r.collectedFeeId, r.feeStatus]),
            );
            return paidIds.length > 0 && paidIds.every((id) => statusById.get(id) === "SETTLED");
          },
          onPaid: () => {
            setPayingTenant(null);
            message.success(t("invoice:feePay.paidSuccess"));
            onPaid();
            onClose();
          },
          onPending: () => {
            setPayingTenant(null);
            message.success(t("invoice:feePay.pendingConfirm"));
            onPaid();
            onClose();
          },
        });
      } else {
        setPayingTenant(null);
        message.success(t("invoice:feePay.requestCreated"));
        onPaid();
        onClose();
      }
    } catch (e) {
      setPayingTenant(null);
      handleError(e);
      // The bank's answer is unknown and the payment is under review: close the modal and
      // refresh instead of leaving a Pay button that invites a second transfer.
      if ((e as { code?: string } | null)?.code === "FEE_PAYMENT_PENDING") {
        onPaid();
        onClose();
      }
    }
  };

  return (
    <>
      {poll.polling && (
        <Portal>
          <Overlay fixed zIndex={3000} backgroundOpacity={0.6} blur={2}>
            <Stack align="center" justify="center" h="100%" gap="md">
              <Loader size="lg" color="white" />
              <Text c="white" fw={600}>
                {t("invoice:feePay.confirming")}
              </Text>
              <Text c="gray.7" size="sm" ta="center" maw={360}>
                {t("invoice:feePay.confirmingHint")}
              </Text>
            </Stack>
          </Overlay>
        </Portal>
      )}
      <Modal
        opened={opened}
        onClose={onClose}
        withCloseButton={false}
        size="lg"
        closeOnEscape={!poll.polling}
        closeOnClickOutside={!poll.polling}
        classNames={{ body: modalChrome.modalBodyFlush }}
      >
        <AppModalHeader
          title={t("invoice:feePay.title")}
          onClose={poll.polling ? undefined : onClose}
        />

        <Box className={classes.main}>
          {loading ? (
            <Group justify="center" py="xl">
              <Loader size="sm" />
            </Group>
          ) : isEmpty ? (
            <Stack gap="md" py="sm">
              <Text size="sm" c="dimmed">
                {t("invoice:feePay.noUnpaidFees")}
              </Text>
            </Stack>
          ) : (
            <Stack gap="md">
              <Select
                label={t("invoice:feePay.debitAccount")}
                description={t("invoice:feePay.debitAccountDesc")}
                placeholder={t("invoice:feePay.debitAccountPlaceholder")}
                data={debitOptions}
                value={debitId}
                onChange={setDebitId}
              />
              {!debit ? (
                <Text size="xs" c="dimmed">
                  {t("invoice:feePay.selectAccountFirst")}
                </Text>
              ) : null}

              {groups.map((g) => (
                <Card key={g.tenantId} withBorder padding="md" radius="md">
                  <Stack gap="sm">
                    <Group justify="space-between" wrap="nowrap" gap="xs">
                      <Text fw={600} truncate>
                        {collectorName(g)}
                      </Text>
                      <Badge variant="light" color="gray" radius="sm">
                        {g.fees.length}
                      </Badge>
                    </Group>
                    <Divider />

                    <Stack gap="sm">
                      {g.fees.map((f) => (
                        <FeePayLine key={f.collectedFeeId} fee={f} />
                      ))}
                    </Stack>

                    <Divider />
                    <Group justify="space-between" align="center">
                      <Text fw={600}>{t("invoice:feePay.totalDue")}</Text>
                      <Text fw={650} size="lg" style={{ fontVariantNumeric: "tabular-nums" }}>
                        {money(g.totalAmount, g.feeCurrency)}
                      </Text>
                    </Group>

                    {g.tenantReceiveAccount ? (
                      <Group gap="xs" wrap="nowrap" align="center">
                        <BankIcon size={18} />
                        <Text size="sm" c="dimmed" truncate>
                          {t("invoice:feePay.receiveAccount")}:{" "}
                          {g.tenantReceiveAccount.bankName ?? g.tenantReceiveAccount.bankCode} ·{" "}
                          {g.tenantReceiveAccount.accountNumber} ·{" "}
                          {g.tenantReceiveAccount.accountName}
                        </Text>
                      </Group>
                    ) : (
                      <Alert color="yellow" variant="light" icon={<WarningIcon size={16} />}>
                        {t("invoice:feePay.noReceiveAccount")}
                      </Alert>
                    )}
                    <Group justify="flex-end">
                      <Button
                        onClick={() => payGroup(g)}
                        disabled={!g.tenantReceiveAccount || !debit || payingTenant !== null}
                        loading={payingTenant === g.tenantId}
                      >
                        {t("invoice:feePay.pay")}
                      </Button>
                    </Group>
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </Box>

        <ModalFooter>
          <Button variant="default" onClick={onClose} fullWidth={isEmpty}>
            {t("common:close")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
